import React, { useEffect, useState } from 'react';
import { Panel, PanelGroup, PanelResizeHandle } from "react-resizable-panels";
import { EditIcon, UploadCloudIcon, SettingsIcon, LayoutIcon, Undo2Icon, Redo2Icon, Sparkles } from "lucide-react";
import styled from "styled-components";
import { Button } from "semantic-ui-react";
import { SaveStatusIndicator } from "./SaveStatusIndicator";
import { FormProvider } from "../fluidComponents/fluidForm/FormContext";
import { FluidChat } from "../fluidComponents/fluidForm/FluidChat.component";
import { FluidForm } from "../shared.types";
import { buildFormConfigItself } from "../../shared";
import { Tabs } from "../components/Tabs";
import { PublishTabContent } from "./PublishTabContent";
import { InterfaceTabContent } from './interfacePanel/InterfaceTabContent';
import ActionsTabContent from './ActionsTabContent';
import { getFormInstance, useFormInstanceListener, useRegisterFormInstance } from '../fluidComponents/fluidForm/FormInstance.service';
import { WorkspaceInstances } from './formInstances';
import { store } from '../fluidComponents/fluidForm/Store';
import { BuilderTabContent } from './interfacePanel/BuilderTabContent';
const PanelWrapper = styled.div.attrs({
    className: 'builder-panel-wrapper'
}) `
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;
const PanelContent = styled.div.attrs({
    className: 'builder-panel-content'
}) `
  flex: 1;
  padding: 1rem;
  overflow-y: auto;
  overflow-x: hidden;
  background: white;
  
  /* Custom scrollbar styling */
  &::-webkit-scrollbar {
    width: 8px;
  }
  
  &::-webkit-scrollbar-track {
    background: #f1f1f1;
  }
  
  &::-webkit-scrollbar-thumb {
    background: #c1c1c1;
    border-radius: 4px;
  }
  
  &::-webkit-scrollbar-thumb:hover {
    background: #a8a8a8;
  }
`;
const TabsPanelContent = styled.div.attrs({
    className: 'builder-tabs-panel-content'
}) `
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  background: white;
  
  /* Custom scrollbar styling */
  &::-webkit-scrollbar {
    width: 8px;
  }
  
  &::-webkit-scrollbar-track {
    background: #f1f1f1;
  }
  
  &::-webkit-scrollbar-thumb {
    background: #c1c1c1;
    border-radius: 4px;
  }
  
  &::-webkit-scrollbar-thumb:hover {
    background: #a8a8a8;
  }
`;
const ResizeHandle = styled(PanelResizeHandle) `
  background: #ffedd5;
  border-bottom: 1px solid #fed7aa;
  transition: background 0.2s ease;
  position: relative;

  &:hover {
    background: #f97316;
  }

  &[data-resize-handle-active] {
    background: #ea580c;
  }

  &[data-panel-group-direction="vertical"] {
    height: 10px;
    cursor: row-resize;

    &::after {
      content: '';
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      width: 24px;
      height: 6px;
      background: rgba(234, 88, 12, 0.5);
      border-radius: 2px;
    }
  }

  &:hover::after {
    background: rgba(255, 255, 255, 0.8);
  }
`;
const BuilderChatIcon = () => (React.createElement("div", { className: "w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center mr-3" },
    React.createElement(Sparkles, { className: "w-6 h-6 text-orange-600" })));
export const BuilderPanelGroup = ({ configRecord, saveStatus, onConfigRecordChange, canUndo, canRedo, onUndo, onRedo, headerExtraActions, onTabSwitcherReady }) => {
    const [activeTab, setActiveTab] = useState('formConfig');
    useEffect(() => {
        onTabSwitcherReady?.(setActiveTab);
    }, []);
    // Allow external callers (like the BuilderTour) to request opening the Publish tab
    // without relying on DOM clicks that may be blocked by overlays.
    useEffect(() => {
        const handler = () => setActiveTab('publish');
        window.addEventListener('ff-open-publish-tab', handler);
        return () => {
            window.removeEventListener('ff-open-publish-tab', handler);
        };
    }, []);
    const previewFormKey = configRecord.formKey;
    const [autoSendMessage, setAutoSendMessage] = useState(undefined);
    useEffect(() => {
        const key = `ff_start_prompt:${previewFormKey}`;
        const stored = sessionStorage.getItem(key);
        if (stored) {
            sessionStorage.removeItem(key);
            setAutoSendMessage(stored);
        }
        else {
            setAutoSendMessage(undefined);
        }
    }, [previewFormKey]);
    let composedConfig = new FluidForm(buildFormConfigItself());
    //const composedFormInstance = useRef(registerFormInstance({instanceId: 'FormBuilder.ComposedForm', form: composedConfig})).current;
    const composedFormInstance = useRegisterFormInstance({ instanceId: 'FormBuilder.ComposedForm', form: composedConfig });
    useEffect(() => {
        getFormInstance(composedFormInstance).dispatch("setSubmissionId", configRecord.formKey + "-builder");
    }, [composedFormInstance]);
    useFormInstanceListener(WorkspaceInstances.formConfig, (event) => {
        if (event.type == "chatbotService.messageReceived") {
            //Forward the message to the composed form so that the chatbot service can handle it
            let message = event.value[0];
            getFormInstance(composedFormInstance).chatbotService.dispatch("messageReceived", message);
        }
    });
    useEffect(() => {
        let form = getFormInstance(WorkspaceInstances.formConfig)?.getFluidForm();
        if (!form) {
            return;
        }
        form.mergeAdditionalConfigValue("styleConfig", {
            fieldStylesConfigurations: [
                {
                    configName: "fields",
                    visibleFields: ['label', 'type', 'required']
                },
                {
                    configName: "fields.fields",
                    visibleFields: ['label', 'type', 'required']
                },
                {
                    configName: "fields.options",
                    visibleFields: ['text']
                },
                {
                    configName: "fields.fields.options",
                    visibleFields: ['text']
                }
            ]
        });
    }, []);
    useFormInstanceListener(composedFormInstance, (event) => {
        if (event.type == "onFormUpdated") {
            let config = getFormInstance(composedFormInstance).getFormSubmission();
            store.dispatch(WorkspaceInstances.formConfig, 'mergeSubmissionData', config);
            onConfigRecordChange({
                formConfig: {
                    ...config,
                    key: configRecord.formKey
                }
            });
        }
    });
    const handleMessageReceived = (message) => {
        //Data should contain the fields so we should update the form config
        let messageData = message?.data;
        let updatedConfigRecord = {};
        let firstAffectedFormKey = null;
        Object.entries(messageData).forEach(([key, value]) => {
            if (WorkspaceInstances[key]) {
                store.dispatch(WorkspaceInstances[key], 'mergeSubmissionData', value);
                updatedConfigRecord[key] = getFormInstance(WorkspaceInstances[key]).getFormSubmission();
                // Remember the first affected form key to switch tabs
                if (!firstAffectedFormKey) {
                    firstAffectedFormKey = key;
                }
            }
        });
        onConfigRecordChange(updatedConfigRecord);
        // Switch to the tab of the affected form
        if (firstAffectedFormKey) {
            setActiveTab(firstAffectedFormKey);
        }
    };
    const headerActions = (React.createElement("div", { style: { display: "flex", alignItems: "center", gap: 8, padding: "8px" } },
        React.createElement("span", { style: { display: "inline-flex" }, title: "Undo" },
            React.createElement(Button, { size: "small", onClick: onUndo, disabled: !canUndo, basic: true, icon: true, "aria-label": "Undo" },
                React.createElement(Undo2Icon, { size: 16 }))),
        React.createElement("span", { style: { display: "inline-flex" }, title: "Redo" },
            React.createElement(Button, { size: "small", onClick: onRedo, disabled: !canRedo, basic: true, icon: true, "aria-label": "Redo" },
                React.createElement(Redo2Icon, { size: 16 }))),
        React.createElement(SaveStatusIndicator, { status: saveStatus }),
        headerExtraActions));
    const tabs = [
        {
            id: 'formConfig',
            label: 'Builder',
            icon: EditIcon,
            description: 'Configure fields & settings',
            content: React.createElement(BuilderTabContent, { configRecord: configRecord, onConfigRecordChange: onConfigRecordChange })
        },
        {
            id: 'styleConfig',
            label: 'Interface',
            icon: LayoutIcon,
            description: 'Choose layout, styling, and input options',
            content: React.createElement(InterfaceTabContent, { configRecord: configRecord, onConfigRecordChange: onConfigRecordChange })
        },
        {
            id: 'actionsConfig',
            label: 'Actions',
            icon: SettingsIcon,
            description: 'Define where form data is sent',
            content: React.createElement(ActionsTabContent, { configRecord: configRecord, onChange: onConfigRecordChange })
        },
        {
            id: 'publish',
            label: 'Publish',
            icon: UploadCloudIcon,
            description: 'Publish, embed, & and share',
            content: React.createElement(PublishTabContent, { formKey: previewFormKey, onConfigRecordChange: onConfigRecordChange })
        }
    ];
    // let [composedConfig, setComposedConfig] = useState<FluidForm>(() => {
    //     return composedConfig;
    // });
    return (React.createElement(PanelWrapper, null,
        React.createElement(PanelGroup, { autoSaveId: "builder-vertical", direction: "vertical", style: { height: '100%' } },
            React.createElement(Panel, { defaultSize: 70, minSize: 30, maxSize: 80 },
                React.createElement(PanelWrapper, null,
                    React.createElement(TabsPanelContent, { "data-tour-id": "builder-canvas" },
                        React.createElement(Tabs, { tabs: tabs, activeTab: activeTab, onTabChange: setActiveTab, actions: headerActions })))),
            React.createElement(ResizeHandle, null),
            React.createElement(Panel, { defaultSize: 30, minSize: 20, maxSize: 70, "data-tour-id": "builder-chat-panel" },
                React.createElement(PanelWrapper, null,
                    React.createElement(PanelContent, { style: { padding: 0 } },
                        React.createElement(FormProvider, { formInstance: composedFormInstance },
                            React.createElement(FluidChat, { instanceId: composedFormInstance, formKey: "workspace-form", onMessageReceived: handleMessageReceived, initialMessage: "Hi! I can help you configure your form. What would you like to change?", autoSendMessage: autoSendMessage, chatPath: "multiform-chat", autoStartForm: false, title: "Build with AI", subtitle: "Chat to modify your form instantly", icon: BuilderChatIcon, headerStyle: { background: '#fffbf6', borderBottom: '1px solid #fed7aa' }, inputFooterStyle: { background: '#fffbf6', borderTop: '1px solid #fed7aa' }, getCurrentData: () => ({
                                    formConfig: configRecord.formConfig,
                                    styleConfig: configRecord.styleConfig,
                                    actionsConfig: configRecord.actionsConfig
                                }), hideClearButton: true }))))))));
};
